import React from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import Select from 'react-select';
import { PaymentTransaction } from '../../../api/superAdminPayments/paymentsService';

interface FilterSectionProps {
  filters: {
    ageCategory: string;
    status: string;
    teams: string;
    tournaments: string;
  };
  onFilterChange: (filterType: string, value: string) => void;
  transactions: PaymentTransaction[];
}

const FilterSection: React.FC<FilterSectionProps> = ({ 
  filters, 
  onFilterChange, 
  transactions 
}) => {
  // Generate filter options from transactions data
  const getAgeCategoryOptions = () => {
    const categories = new Set<string>();
    transactions.forEach(transaction => {
      // Use teamInfo.ageCategory first (more reliable), then fallback to tournamentInfo.ageCategory
      const ageCategory = transaction.teamInfo?.ageCategory || transaction.tournamentInfo?.ageCategory;
      if (ageCategory) {
        categories.add(ageCategory);
      } else if (transaction.tournamentInfo.name) {
        // Fallback to regex extraction from tournament name
        const match = transaction.tournamentInfo.name.match(/U\d+/);
        if (match) {
          categories.add(match[0]);
        }
      }
    });
    
    const options = Array.from(categories).sort().map(category => ({
      value: category,
      label: category
    }));
    
    return [{ value: 'All', label: 'All' }, ...options];
  };

  const getStatusOptions = () => [
    { value: 'All', label: 'All' },
    { value: 'paid', label: 'Paid' },
    { value: 'failed', label: 'Failed' },
    { value: 'pending', label: 'Pending' }
  ];

  const getTeamsOptions = () => {
    const teams = new Set<string>();
    transactions.forEach(transaction => {
      if (transaction.teamInfo.name) {
        teams.add(transaction.teamInfo.name);
      }
    });
    
    const options = Array.from(teams).sort().map(team => ({
      value: team,
      label: team
    }));
    
    return [{ value: 'All', label: 'All' }, ...options];
  };

  const getTournamentsOptions = () => {
    const tournaments = new Set<string>();
    transactions.forEach(transaction => {
      if (transaction.tournamentInfo.name) {
        tournaments.add(transaction.tournamentInfo.name);
      }
    });
    
    const options = Array.from(tournaments).sort().map(tournament => ({
      value: tournament,
      label: tournament
    }));
    
    return [{ value: 'All', label: 'All' }, ...options];
  };

  const customSelectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      border: '1px solid #dee2e6',
      borderRadius: '8px',
      minHeight: '40px',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 119, 249, 0.25)' : 'none',
      borderColor: state.isFocused ? '#0077F9' : '#dee2e6',
      '&:hover': {
        borderColor: '#0077F9'
      }
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
      position: 'absolute'
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#0077F9' : state.isFocused ? '#f8f9fa' : 'white',
      color: state.isSelected ? 'white' : '#212529',
      '&:hover': {
        backgroundColor: state.isSelected ? '#0077F9' : '#f8f9fa'
      }
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: '#212529',
      fontSize: '14px'
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#6c757d',
      fontSize: '14px'
    })
  };

  return (
    <div className="filter-section mb-4">
      <Row>
        <Col lg={3} md={6} className="mb-3">
          <Form.Group>
            <Form.Label className="fw-semibold mb-2" style={{ fontSize: '14px', color: '#495057' }}>
              Age Category
            </Form.Label>
            <Select
              value={{ value: filters.ageCategory, label: filters.ageCategory }}
              onChange={(option) => onFilterChange('ageCategory', option?.value || 'All')}
              options={getAgeCategoryOptions()}
              styles={customSelectStyles}
              placeholder="Select Age Category"
              isClearable={false}
            />
          </Form.Group>
        </Col>
        
        <Col lg={3} md={6} className="mb-3">
          <Form.Group>
            <Form.Label className="fw-semibold mb-2" style={{ fontSize: '14px', color: '#495057' }}>
              Status
            </Form.Label>
            <Select
              value={{ value: filters.status, label: filters.status }}
              onChange={(option) => onFilterChange('status', option?.value || 'All')}
              options={getStatusOptions()}
              styles={customSelectStyles}
              placeholder="Select Status"
              isClearable={false}
            />
          </Form.Group>
        </Col>
        
        <Col lg={3} md={6} className="mb-3">
          <Form.Group>
            <Form.Label className="fw-semibold mb-2" style={{ fontSize: '14px', color: '#495057' }}>
              Teams
            </Form.Label>
            <Select
              value={{ value: filters.teams, label: filters.teams }}
              onChange={(option) => onFilterChange('teams', option?.value || 'All')}
              options={getTeamsOptions()}
              styles={customSelectStyles}
              placeholder="Select Team"
              isClearable={false}
            />
          </Form.Group>
        </Col>
        
        <Col lg={3} md={6} className="mb-3">
          <Form.Group>
            <Form.Label className="fw-semibold mb-2" style={{ fontSize: '14px', color: '#495057' }}>
              Tournaments
            </Form.Label>
            <Select
              value={{ value: filters.tournaments, label: filters.tournaments }}
              onChange={(option) => onFilterChange('tournaments', option?.value || 'All')}
              options={getTournamentsOptions()}
              styles={customSelectStyles}
              placeholder="Select Tournament"
              isClearable={false}
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

export default FilterSection; 